import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import { blogService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { getApiErrorMessage } from '../utils/apiErrors';
import useTenantStore from '../store/tenantStore';
import {
  Card, Badge, Button, Modal, Input, Field, Select, EmptyState, Spinner,
} from '../components/ui';

const FONT_OPTIONS = [
  ['system', 'System'], ['serif', 'Serif'], ['mono', 'Monospace'], ['rounded', 'Rounded'],
];
const LAYOUT_OPTIONS = [
  ['classic', 'Classic'], ['magazine', 'Magazine (grid)'], ['minimal', 'Minimal (centered)'],
];
const THEME_OPTIONS = [
  { id: 'editorial', name: 'Editorial', description: 'Elegant typography for thoughtful long-form writing.', colors: ['#172554', '#2563EB', '#F8FAFC'] },
  { id: 'creator', name: 'Creator', description: 'Friendly, vibrant and personal with rounded cards.', colors: ['#7C3AED', '#EC4899', '#FFF7ED'] },
  { id: 'academy', name: 'Academy', description: 'Structured and trustworthy for educational content.', colors: ['#0F766E', '#14B8A6', '#F0FDFA'] },
  { id: 'journal', name: 'Journal', description: 'A refined, minimal reading-first publication.', colors: ['#292524', '#A16207', '#FAFAF9'] },
  { id: 'bold', name: 'Bold', description: 'High-impact editorial cards and strong headlines.', colors: ['#111827', '#F97316', '#FFFBEB'] },
];
const COLOR_FIELDS = [
  ['primary_color', 'Primary color'],
  ['secondary_color', 'Secondary color'],
  ['background_color', 'Background color'],
  ['text_color', 'Text color'],
];

const EMPTY_POST = { title: '', content: '', excerpt: '', category: '', tags: '', is_featured: false, seo_title: '', seo_description: '', status: 'draft' };
const EMPTY_PAGE = { title: '', slug: '', content: '', status: 'draft', show_in_navigation: true, navigation_order: 0, seo_title: '', seo_description: '' };
const EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ font: [] }, { size: ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ script: 'sub' }, { script: 'super' }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ direction: 'rtl' }, { align: [] }],
    ['blockquote', 'code-block'],
    ['link'],
    ['clean'],
  ],
};

const BlockEmbed = Quill.import('blots/block/embed');
class AttachmentBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.setAttribute('href', value.url);
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
    node.setAttribute('download', '');
    node.textContent = `📎 ${value.name}`;
    return node;
  }

  static value(node) {
    return { url: node.getAttribute('href'), name: node.textContent.replace(/^📎\s*/, '') };
  }
}
AttachmentBlot.blotName = 'attachment';
AttachmentBlot.tagName = 'a';
AttachmentBlot.className = 'blog-attachment';
Quill.register(AttachmentBlot);

function BlogConfigForm({ blog, onSaved }) {
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const logoInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const [form, setForm] = useState({ ...blog, social_links: blog.social_links || {} });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = new FormData();
      const scalarFields = [
        'subdomain', 'dns_mode', 'external_domain', 'title', 'tagline',
        'primary_color', 'secondary_color', 'background_color', 'text_color',
        'font_family', 'layout_template', 'theme_template', 'footer_text',
        'language',
      ];
      scalarFields.forEach((f) => payload.append(f, form[f] || ''));
      payload.append('comments_enabled', form.comments_enabled ? 'true' : 'false');
      payload.append('show_author', form.show_author ? 'true' : 'false');
      payload.append('show_reading_time', form.show_reading_time ? 'true' : 'false');
      payload.append('social_links', JSON.stringify(form.social_links || {}));
      if (logoFile) payload.append('logo', logoFile);
      if (coverFile) payload.append('cover_image', coverFile);

      const res = await blogService.updateBlog(blog.id, payload);
      addNotification(t('Blog settings saved'), 'success');
      setLogoFile(null);
      setCoverFile(null);
      onSaved(res.data);
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, t('Failed to save blog settings')), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="space-y-5">
      <h3 className="font-semibold text-gray-900">{t('Blog Setup')}</h3>

      <Field label={t('Subdomain')} required hint={t('Your blog will be reachable at this-name.lmcid.com')}>
        <div className="flex items-center gap-2">
          <Input
            value={form.subdomain || ''}
            onChange={(e) => setForm({ ...form, subdomain: e.target.value.toLowerCase() })}
            placeholder="johndoe"
          />
          <span className="text-sm text-gray-500 whitespace-nowrap">.lmcid.com</span>
        </div>
      </Field>

      <Field label={t('Domain type')}>
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="radio" checked={form.dns_mode === 'subdomain'}
              onChange={() => setForm({ ...form, dns_mode: 'subdomain' })}
            />
            {t('Use the subdomain above')}
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio" checked={form.dns_mode === 'external'}
              onChange={() => setForm({ ...form, dns_mode: 'external' })}
            />
            {t('Point my own domain here')}
          </label>
        </div>
        {form.dns_mode === 'external' && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-2">
            <Input
              value={form.external_domain || ''}
              onChange={(e) => setForm({ ...form, external_domain: e.target.value })}
              placeholder="blog.myschool.com"
            />
            <p className="text-xs text-gray-500">
              {t('Not active yet — this is a preview of what will be needed. Once support for custom domains ships, you\'ll add a CNAME record pointing "{{domain}}" to lmcid.com, plus a TXT record we\'ll provide for verification.', { domain: form.external_domain || 'your-domain' })}
            </p>
          </div>
        )}
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t('Blog Title')}>
          <Input value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </Field>
        <Field label={t('Tagline')}>
          <Input value={form.tagline || ''} onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
        </Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t('Logo')}>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
              {logoPreview || form.logo ? <img src={logoPreview || form.logo} alt="" className="w-full h-full object-cover" /> : <span className="text-xs text-gray-400">{t('None')}</span>}
            </div>
            <input type="file" accept="image/*" ref={logoInputRef} className="hidden" onChange={(e) => {
              const file = e.target.files[0];
              if (!file) return;
              setLogoFile(file);
              setLogoPreview(URL.createObjectURL(file));
            }} />
            <Button variant="secondary" size="sm" onClick={() => logoInputRef.current?.click()}>{t('Upload')}</Button>
          </div>
        </Field>
        <Field label={t('Cover Image')}>
          <div className="flex items-center gap-3">
            <div className="w-20 h-14 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center shrink-0">
              {coverPreview || form.cover_image ? <img src={coverPreview || form.cover_image} alt="" className="w-full h-full object-cover" /> : <span className="text-xs text-gray-400">{t('None')}</span>}
            </div>
            <input type="file" accept="image/*" ref={coverInputRef} className="hidden" onChange={(e) => {
              const file = e.target.files[0];
              if (!file) return;
              setCoverFile(file);
              setCoverPreview(URL.createObjectURL(file));
            }} />
            <Button variant="secondary" size="sm" onClick={() => coverInputRef.current?.click()}>{t('Upload')}</Button>
          </div>
        </Field>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">{t('Choose a theme')}</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
          {THEME_OPTIONS.map((theme) => (
            <button
              type="button" key={theme.id}
              onClick={() => setForm({
                ...form,
                theme_template: theme.id,
                secondary_color: theme.colors[0],
                primary_color: theme.colors[1],
                background_color: theme.colors[2],
                text_color: theme.id === 'bold' ? '#111827' : theme.colors[0],
              })}
              className={`rounded-xl border-2 p-3 text-left transition ${form.theme_template === theme.id ? 'border-primary-500 bg-primary-50 shadow-sm' : 'border-gray-200 hover:border-primary-200'}`}
            >
              <div className="flex h-16 overflow-hidden rounded-lg mb-3">
                {theme.colors.map((color, index) => <span key={color} className={index === 0 ? 'w-1/2' : 'w-1/4'} style={{ backgroundColor: color }} />)}
              </div>
              <p className="text-sm font-semibold text-gray-900">{t(theme.name)}</p>
              <p className="mt-1 text-[11px] leading-4 text-gray-500">{t(theme.description)}</p>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('Colors')}</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {COLOR_FIELDS.map(([field, label]) => (
            <label key={field} className="flex items-center gap-2 border border-gray-200 rounded-lg p-2">
              <input
                type="color" value={form[field] || '#000000'}
                onChange={(e) => setForm({ ...form, [field]: e.target.value.toUpperCase() })}
                className="h-8 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
              />
              <span className="text-xs text-gray-600">{t(label)}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label={t('Blog language')} hint={t('Controls the public blog interface and reading direction')}>
          <Select value={form.language || 'en'} onChange={(e) => setForm({ ...form, language: e.target.value })}>
            <option value="en">English</option>
            <option value="fr">Français</option>
            <option value="ar">العربية</option>
          </Select>
        </Field>
        <Field label={t('Font')}>
          <Select value={form.font_family} onChange={(e) => setForm({ ...form, font_family: e.target.value })}>
            {FONT_OPTIONS.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
          </Select>
        </Field>
        <Field label={t('Layout')}>
          <Select value={form.layout_template} onChange={(e) => setForm({ ...form, layout_template: e.target.value })}>
            {LAYOUT_OPTIONS.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
          </Select>
        </Field>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('Social Links')}</label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {['facebook', 'instagram', 'youtube', 'linkedin', 'tiktok', 'website'].map((key) => (
            <Input
              key={key}
              value={form.social_links?.[key] || ''}
              placeholder={key}
              onChange={(e) => setForm({ ...form, social_links: { ...form.social_links, [key]: e.target.value } })}
            />
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox" checked={form.comments_enabled}
          onChange={(e) => setForm({ ...form, comments_enabled: e.target.checked })}
        />
        {t('Allow comments (any logged-in L\'mcid user can comment)')}
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={form.show_author ?? true} onChange={(e) => setForm({ ...form, show_author: e.target.checked })} />
          {t('Show author on articles')}
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={form.show_reading_time ?? true} onChange={(e) => setForm({ ...form, show_reading_time: e.target.checked })} />
          {t('Show estimated reading time')}
        </label>
      </div>

      <Field label={t('Footer text')}>
        <Input value={form.footer_text || ''} onChange={(e) => setForm({ ...form, footer_text: e.target.value })} placeholder={t('A short message shown at the bottom of every page')} />
      </Field>

      <div className="flex justify-end">
        <Button loading={saving} onClick={save}>{t('Save Blog Settings')}</Button>
      </div>
    </Card>
  );
}

function RichBlogEditor({ value, onChange, placeholder }) {
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState('');
  const [expanded, setExpanded] = useState(false);

  const plainText = (value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
  const characterCount = plainText.length;
  const wordCount = plainText ? plainText.split(/\s+/).length : 0;

  const setTextDirection = (direction) => {
    const editor = editorRef.current?.getEditor();
    if (!editor) return;
    editor.focus();
    const range = editor.getSelection(true) || { index: Math.max(0, editor.getLength() - 1), length: 0 };
    editor.formatLine(range.index, range.length, 'direction', direction === 'rtl' ? 'rtl' : false, 'user');
    editor.formatLine(range.index, range.length, 'align', direction === 'rtl' ? 'right' : false, 'user');
  };

  const uploadAndInsert = async (file, kind) => {
    if (!file) return;
    setUploading(kind);
    try {
      const { data } = await blogService.uploadAsset(file);
      const editor = editorRef.current?.getEditor();
      if (!editor) return;
      const range = editor.getSelection(true) || { index: Math.max(0, editor.getLength() - 1) };
      if (data.asset_type === 'image') {
        editor.insertEmbed(range.index, 'image', data.file, 'user');
        editor.insertText(range.index + 1, '\n', 'user');
        editor.setSelection(range.index + 2, 0, 'silent');
      } else {
        editor.insertEmbed(range.index, 'attachment', { url: data.file, name: data.original_name }, 'user');
        editor.insertText(range.index + 1, '\n', 'user');
        editor.setSelection(range.index + 2, 0, 'silent');
      }
      addNotification(t(kind === 'image' ? 'Image inserted' : 'File attached'), 'success');
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, t('Failed to upload file')), 'error');
    } finally {
      setUploading('');
      if (imageInputRef.current) imageInputRef.current.value = '';
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return <div className={`${expanded ? 'fixed inset-3 z-[80] flex flex-col shadow-2xl sm:inset-6' : ''} overflow-hidden rounded-xl border border-gray-200 bg-white`}>
    <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
      <input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={(e) => uploadAndInsert(e.target.files[0], 'image')} />
      <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,.zip" className="hidden" onChange={(e) => uploadAndInsert(e.target.files[0], 'file')} />
      <Button type="button" size="sm" variant="secondary" loading={uploading === 'image'} disabled={Boolean(uploading)} onClick={() => imageInputRef.current?.click()}>
        {t('Insert image')}
      </Button>
      <Button type="button" size="sm" variant="secondary" loading={uploading === 'file'} disabled={Boolean(uploading)} onClick={() => fileInputRef.current?.click()}>
        {t('Attach file')}
      </Button>
      <span className="mx-1 hidden h-6 w-px bg-gray-300 sm:block" />
      <Button type="button" size="sm" variant="secondary" onClick={() => setTextDirection('ltr')} title={t('Write left to right')}>
        LTR
      </Button>
      <Button type="button" size="sm" variant="secondary" onClick={() => setTextDirection('rtl')} title={t('Write right to left')}>
        RTL
      </Button>
      <button
        type="button"
        onClick={() => setExpanded((current) => !current)}
        className="ml-auto rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-200 hover:text-gray-900"
      >
        {expanded ? t('Exit full screen') : t('Expand editor')}
      </button>
    </div>
    <ReactQuill
      ref={editorRef}
      theme="snow"
      modules={EDITOR_MODULES}
      value={value}
      onChange={onChange}
      placeholder={placeholder || t('Write your article here...')}
      className={`blog-content-editor ${expanded ? 'blog-content-editor-expanded flex-1 overflow-y-auto' : ''}`}
    />
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-500">
      <span>{t('Images are inserted at the cursor. Documents become download cards.')}</span>
      <span className="tabular-nums">{t('{{count}} words', { count: wordCount })} · {t('{{count}} characters', { count: characterCount })} · {t('No character limit')}</span>
    </div>
  </div>;
}

function PostEditorModal({ open, post, onClose, onSaved }) {
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const mediaInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const [form, setForm] = useState(EMPTY_POST);
  const [coverFile, setCoverFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  useEffect(() => {
    setForm(post ? {
      ...EMPTY_POST, ...post, tags: (post.tags || []).join(', '),
    } : EMPTY_POST);
    setCoverFile(null);
  }, [post, open]);

  const save = async () => {
    if (!form.title.trim()) {
      addNotification(t('Enter a title'), 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = new FormData();
      ['title', 'content', 'excerpt', 'category', 'seo_title', 'seo_description', 'status'].forEach((key) => payload.append(key, form[key] || ''));
      payload.append('tags', JSON.stringify(form.tags.split(',').map((tag) => tag.trim()).filter(Boolean)));
      payload.append('is_featured', form.is_featured ? 'true' : 'false');
      if (coverFile) payload.append('cover_image', coverFile);
      if (post) {
        const res = await blogService.updatePost(post.id, payload);
        onSaved(res.data);
      } else {
        const res = await blogService.createPost(payload);
        onSaved(res.data);
      }
      addNotification(t('Post saved'), 'success');
    } catch (e) {
      addNotification(getApiErrorMessage(e, t, t('Failed to save post')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const removeMedia = async (mediaId) => {
    try {
      await blogService.deleteMedia(mediaId);
      onSaved({ ...post, media: (post.media || []).filter((item) => item.id !== mediaId) });
      addNotification(t('Media removed'), 'success');
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, t('Failed to remove media')), 'error');
    }
  };

  const uploadMedia = async (e) => {
    const file = e.target.files[0];
    if (!file || !post) return;
    setUploadingMedia(true);
    try {
      const res = await blogService.addMedia(post.id, file);
      onSaved({ ...post, media: [...(post.media || []), res.data] });
      addNotification(t('Media added'), 'success');
    } catch (err) {
      addNotification(getApiErrorMessage(err, t, t('Failed to upload media')), 'error');
    } finally {
      setUploadingMedia(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={post ? t('Edit Post') : t('New Post')}
      size="2xl"
      footer={<>
        <Button variant="secondary" onClick={onClose}>{t('Close')}</Button>
        <Button loading={saving} onClick={save}>{t('Save')}</Button>
      </>}
    >
      <div className="space-y-4">
        <Field label={t('Title')} required>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t('Category')}>
            <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder={t('e.g. Science, Study tips')} />
          </Field>
          <Field label={t('Tags')} hint={t('Separate tags with commas')}>
            <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="biology, revision" />
          </Field>
        </div>
        <Field label={t('Article content')} hint={t('Long-form content with no character limit')}>
          <RichBlogEditor value={form.content} onChange={(content) => setForm({ ...form, content })} />
        </Field>
        <Field label={t('Summary or introduction')} hint={t('Optional. Used on article cards and in search results; around 320 characters is recommended, but not required.')}>
          <textarea rows={5} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm leading-6" />
        </Field>
        <Field label={t('Cover image')}>
          <div className="flex items-center gap-4">
            {(coverFile || post?.cover_image) && <img src={coverFile ? URL.createObjectURL(coverFile) : post.cover_image} alt="" className="h-20 w-32 rounded-lg object-cover" />}
            <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => setCoverFile(e.target.files[0] || null)} />
            <Button variant="secondary" size="sm" onClick={() => coverInputRef.current?.click()}>{t('Choose image')}</Button>
          </div>
        </Field>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} />
          {t('Feature this article on the blog homepage')}
        </label>
        <details className="rounded-xl border border-gray-200 p-4">
          <summary className="cursor-pointer text-sm font-semibold text-gray-800">{t('Search engine settings')}</summary>
          <div className="mt-4 grid gap-4">
            <Field label={t('SEO title')} hint={`${(form.seo_title || '').length}/70`}><Input maxLength={70} value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} /></Field>
            <Field label={t('SEO description')} hint={`${(form.seo_description || '').length}/160`}><textarea maxLength={160} rows={2} value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" /></Field>
          </div>
        </details>
        <Field label={t('Status')}>
          <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="draft">{t('Draft')}</option>
            <option value="published">{t('Published')}</option>
          </Select>
        </Field>

        {post && (
          <Field label={t('Photos & Videos')} hint={t('Save the post first, then add media to it.')}>
            <div className="grid grid-cols-4 gap-2 mb-2">
              {(post.media || []).map((m) => <div key={m.id} className="relative group">
                {m.media_type === 'video' ? <video src={m.file} className="w-full h-16 object-cover rounded" /> : <img src={m.file} alt="" className="w-full h-16 object-cover rounded" />}
                <button type="button" onClick={() => removeMedia(m.id)} className="absolute right-1 top-1 hidden h-6 w-6 rounded-full bg-red-600 text-xs text-white group-hover:block" aria-label={t('Remove')}>×</button>
              </div>)}
            </div>
            <input type="file" accept="image/*,video/*" ref={mediaInputRef} className="hidden" onChange={uploadMedia} />
            <Button size="sm" variant="secondary" loading={uploadingMedia} onClick={() => mediaInputRef.current?.click()}>
              + {t('Add Photo/Video')}
            </Button>
          </Field>
        )}
      </div>
    </Modal>
  );
}

function PageEditorModal({ open, page, onClose, onSaved }) {
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_PAGE);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(page ? { ...EMPTY_PAGE, ...page } : EMPTY_PAGE); }, [page, open]);
  const save = async () => {
    if (!form.title.trim()) return addNotification(t('Enter a title'), 'error');
    setSaving(true);
    try {
      const { data } = page ? await blogService.updatePage(page.id, form) : await blogService.createPage(form);
      onSaved(data); addNotification(t('Page saved'), 'success');
    } catch (error) { addNotification(getApiErrorMessage(error, t, t('Failed to save page')), 'error'); }
    finally { setSaving(false); }
  };
  return <Modal open={open} onClose={onClose} title={page ? t('Edit Page') : t('New Page')} size="xl" footer={<><Button variant="secondary" onClick={onClose}>{t('Close')}</Button><Button loading={saving} onClick={save}>{t('Save')}</Button></>}>
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"><Field label={t('Page title')} required><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}/></Field><Field label={t('URL slug')} hint={t('Generated from the title if left empty')}><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}/></Field></div>
      <Field label={t('Page content')}><RichBlogEditor value={form.content} onChange={(content) => setForm({ ...form, content })}/></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"><Field label={t('Status')}><Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><option value="draft">{t('Draft')}</option><option value="published">{t('Published')}</option></Select></Field><Field label={t('Navigation order')}><Input type="number" min="0" value={form.navigation_order} onChange={(e) => setForm({ ...form, navigation_order: Number(e.target.value) })}/></Field></div>
      <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={form.show_in_navigation} onChange={(e) => setForm({ ...form, show_in_navigation: e.target.checked })}/>{t('Show this page in the blog navigation')}</label>
      <details className="rounded-xl border border-gray-200 p-4"><summary className="cursor-pointer text-sm font-semibold">{t('Search engine settings')}</summary><div className="mt-4 space-y-4"><Field label={t('SEO title')}><Input maxLength={70} value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })}/></Field><Field label={t('SEO description')}><textarea rows={2} maxLength={160} value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"/></Field></div></details>
    </div>
  </Modal>;
}

function TeacherBlog() {
  const { addNotification } = useNotification();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const tenantSchool = useTenantStore((state) => state.school);
  const [loading, setLoading] = useState(true);
  const [blog, setBlog] = useState(null);
  const [posts, setPosts] = useState([]);
  const [pages, setPages] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [requesting, setRequesting] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [pageEditorOpen, setPageEditorOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);

  const fetchAll = () => {
    setLoading(true);
    blogService.getMyBlog().then(async ({ data }) => {
      const list = data.results || data || [];
      const current = list[0] || null;
      setBlog(current);
      if (current?.status === 'approved') {
        const [postResponse, pageResponse] = await Promise.all([blogService.getPosts({ blog: current.id }), blogService.getPages()]);
        setPosts(postResponse.data.results || postResponse.data || []);
        setPages(pageResponse.data.results || pageResponse.data || []);
      }
    }).catch((error) => addNotification(getApiErrorMessage(error, t, t('Failed to load blog')), 'error')).finally(() => setLoading(false));
  };
  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const requestBlog = async () => {
    setRequesting(true);
    try { const { data } = await blogService.requestBlog(); setBlog(data); addNotification(t('Blog requested — an administrator will review it'), 'success'); }
    catch (error) { addNotification(getApiErrorMessage(error, t, t('Failed to request a blog')), 'error'); }
    finally { setRequesting(false); }
  };
  const savePost = (saved) => {
    setEditingPost(saved);
    setPosts((current) => current.some((post) => post.id === saved.id) ? current.map((post) => post.id === saved.id ? saved : post) : [saved, ...current]);
  };
  const savePage = (saved) => {
    setEditingPage(saved);
    setPages((current) => current.some((page) => page.id === saved.id) ? current.map((page) => page.id === saved.id ? saved : page) : [...current, saved].sort((a, b) => a.navigation_order - b.navigation_order));
  };
  const removePost = async (post) => {
    if (!(await confirm(t('Delete post "{{title}}"?', { title: post.title })))) return;
    try { await blogService.deletePost(post.id); setPosts((current) => current.filter((item) => item.id !== post.id)); addNotification(t('Post deleted'), 'success'); }
    catch (error) { addNotification(getApiErrorMessage(error, t, t('Failed to delete post')), 'error'); }
  };
  const removePage = async (page) => {
    if (!(await confirm(t('Delete page "{{title}}"?', { title: page.title })))) return;
    try { await blogService.deletePage(page.id); setPages((current) => current.filter((item) => item.id !== page.id)); addNotification(t('Page deleted'), 'success'); }
    catch (error) { addNotification(getApiErrorMessage(error, t, t('Failed to delete page')), 'error'); }
  };
  const togglePostStatus = async (post) => {
    const nextStatus = post.status === 'published' ? 'draft' : 'published';
    try { const { data } = await blogService.updatePost(post.id, { status: nextStatus }); savePost(data); addNotification(t(nextStatus === 'published' ? 'Post published' : 'Post moved to drafts'), 'success'); }
    catch (error) { addNotification(getApiErrorMessage(error, t, t('Failed to update post')), 'error'); }
  };
  const togglePageStatus = async (page) => {
    const nextStatus = page.status === 'published' ? 'draft' : 'published';
    try { const { data } = await blogService.updatePage(page.id, { status: nextStatus }); savePage(data); addNotification(t(nextStatus === 'published' ? 'Page published' : 'Page moved to drafts'), 'success'); }
    catch (error) { addNotification(getApiErrorMessage(error, t, t('Failed to update page')), 'error'); }
  };
  const openPost = (post = null) => { setEditingPost(post); setEditorOpen(true); };
  const openPage = (page = null) => { setEditingPage(page); setPageEditorOpen(true); };
  const tabs = [['overview', 'Overview'], ['posts', 'Posts'], ['pages', 'Pages'], ['appearance', 'Design & settings']];
  const publicUrl = blog?.subdomain
    ? (tenantSchool
      ? `${window.location.origin}/blog/${blog.subdomain}`
      : `https://${blog.subdomain}.lmcid.com`)
    : null;
  const blogIsLive = Boolean(tenantSchool || blog?.tls_status === 'active');

  return <div className="flex h-screen bg-surface-50">
    <Sidebar />
    <div className="flex-1 flex flex-col overflow-hidden">
      <TopNav title={t('Blog Studio')} subtitle={t('Create, publish and shape your own modern publication')} />
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        {loading ? <div className="p-12 flex justify-center"><Spinner /></div> : !blog ? <Card><EmptyState title={t('Create your teacher blog')} description={t('Request your own publication. After approval, you can choose a theme, publish articles and build standalone pages.')} action={<Button loading={requesting} onClick={requestBlog}>{t('Request a Blog')}</Button>} /></Card> : blog.status !== 'approved' ? <Card className="text-center py-10"><Badge tone={blog.status === 'rejected' ? 'red' : 'yellow'}>{t(blog.status)}</Badge><h2 className="mt-4 text-xl font-semibold text-gray-900">{blog.status === 'pending' ? t('Your blog is being reviewed') : t('Your blog request was not approved')}</h2><p className="mx-auto mt-2 max-w-xl text-sm text-gray-500">{blog.status === 'pending' ? t('An administrator will review your request. Your complete Blog Studio will unlock as soon as it is approved.') : blog.rejection_reason}</p></Card> : <div className="mx-auto max-w-7xl space-y-6">
          <section className="overflow-hidden rounded-2xl bg-gradient-to-r from-gray-950 to-primary-900 p-6 text-white shadow-lg sm:p-8">
            <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-center"><div><div className="flex items-center gap-3"><span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">{t('Blog Studio')}</span><Badge tone={blogIsLive ? 'green' : blog.tls_status === 'failed' ? 'red' : 'yellow'}>{blogIsLive ? t('Live & secure') : t('Preparing HTTPS')}</Badge></div><h1 className="mt-4 text-2xl font-bold sm:text-3xl">{blog.title || t('Your publication')}</h1><p className="mt-2 text-sm text-white/65">{blog.tagline || t('Add your title, story and visual identity from Design & settings.')}</p></div>{publicUrl && <a href={publicUrl} target="_blank" rel="noreferrer" className={`inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-semibold ${blogIsLive ? 'bg-white text-gray-900 hover:bg-gray-100' : 'pointer-events-none bg-white/10 text-white/40'}`}>{t('View live blog')} ↗</a>}</div>
          </section>

          <nav className="flex gap-1 overflow-x-auto rounded-xl border border-gray-200 bg-white p-1.5 shadow-sm">{tabs.map(([id, label]) => <button key={id} onClick={() => setActiveTab(id)} className={`whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-semibold transition ${activeTab === id ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>{t(label)}</button>)}</nav>

          {activeTab === 'overview' && <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">{[
              [posts.length, 'Total posts'], [posts.filter((post) => post.status === 'published').length, 'Published'], [posts.filter((post) => post.status === 'draft').length, 'Drafts'], [pages.length, 'Pages'],
            ].map(([value, label]) => <Card key={label}><p className="text-3xl font-bold text-gray-900">{value}</p><p className="mt-1 text-sm text-gray-500">{t(label)}</p></Card>)}</div>
            <div className="grid gap-6 lg:grid-cols-3"><Card className="lg:col-span-2"><div className="flex items-center justify-between"><div><h2 className="font-semibold text-gray-900">{t('Recent content')}</h2><p className="mt-1 text-sm text-gray-500">{t('Continue writing or publish a draft.')}</p></div><Button size="sm" onClick={() => openPost()}>+ {t('New Post')}</Button></div><div className="mt-5 divide-y divide-gray-100">{posts.slice(0, 5).map((post) => <button key={post.id} onClick={() => openPost(post)} className="flex w-full items-center justify-between gap-4 py-3 text-left"><div className="min-w-0"><p className="truncate text-sm font-semibold text-gray-900">{post.title}</p><p className="text-xs text-gray-400">{post.category || t('Uncategorized')} · {new Date(post.updated_at).toLocaleDateString()}</p></div><Badge status={post.status}/></button>)}{!posts.length && <p className="py-8 text-center text-sm text-gray-400">{t('No posts yet')}</p>}</div></Card><Card><h2 className="font-semibold text-gray-900">{t('Quick actions')}</h2><div className="mt-4 grid gap-2"><Button onClick={() => openPost()}>+ {t('Write an article')}</Button><Button variant="secondary" onClick={() => openPage()}>+ {t('Create a page')}</Button><Button variant="secondary" onClick={() => setActiveTab('appearance')}>{t('Customize design')}</Button></div></Card></div>
          </div>}

          {activeTab === 'posts' && <section><div className="mb-5 flex items-center justify-between"><div><h2 className="text-xl font-bold text-gray-900">{t('Articles')}</h2><p className="text-sm text-gray-500">{t('Write, organize and publish your ideas.')}</p></div><Button onClick={() => openPost()}>+ {t('New Post')}</Button></div>{!posts.length ? <Card><EmptyState title={t('No posts yet')} action={<Button onClick={() => openPost()}>+ {t('Write your first article')}</Button>}/></Card> : <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{posts.map((post) => <Card key={post.id} className="overflow-hidden p-0"><div className="h-36 bg-gradient-to-br from-primary-100 to-primary-300">{post.cover_image && <img src={post.cover_image} alt="" className="h-full w-full object-cover"/>}</div><div className="p-5"><div className="flex items-center justify-between gap-3"><span className="text-xs font-semibold text-primary-600">{post.category || t('Article')}</span><Badge status={post.status}/></div><h3 className="mt-3 line-clamp-2 font-bold text-gray-900">{post.title}</h3><p className="mt-2 line-clamp-2 text-sm text-gray-500">{post.excerpt || t('No summary added yet.')}</p><div className="mt-5 flex flex-wrap gap-2"><Button size="sm" variant="secondary" onClick={() => openPost(post)}>{t('Edit')}</Button><Button size="sm" variant="secondary" onClick={() => togglePostStatus(post)}>{post.status === 'published' ? t('Unpublish') : t('Publish')}</Button><Button size="sm" variant="danger" onClick={() => removePost(post)}>{t('Delete')}</Button></div></div></Card>)}</div>}</section>}

          {activeTab === 'pages' && <section><div className="mb-5 flex items-center justify-between"><div><h2 className="text-xl font-bold text-gray-900">{t('Website pages')}</h2><p className="text-sm text-gray-500">{t('Build About, Resources, Contact or any custom page.')}</p></div><Button onClick={() => openPage()}>+ {t('New Page')}</Button></div>{!pages.length ? <Card><EmptyState title={t('No pages yet')} description={t('Create an About page or a resource hub and add it to your blog navigation.')} action={<Button onClick={() => openPage()}>+ {t('Create your first page')}</Button>}/></Card> : <Card className="divide-y divide-gray-100 p-0">{pages.map((page) => <div key={page.id} className="flex flex-col justify-between gap-4 p-5 sm:flex-row sm:items-center"><div><div className="flex items-center gap-2"><h3 className="font-semibold text-gray-900">{page.title}</h3><Badge status={page.status}/>{page.show_in_navigation && <Badge tone="purple">{t('In navigation')}</Badge>}</div><p className="mt-1 text-xs text-gray-400">/{page.slug} · {t('Order')} {page.navigation_order}</p></div><div className="flex flex-wrap gap-2"><Button size="sm" variant="secondary" onClick={() => openPage(page)}>{t('Edit')}</Button><Button size="sm" variant="secondary" onClick={() => togglePageStatus(page)}>{page.status === 'published' ? t('Unpublish') : t('Publish')}</Button><Button size="sm" variant="danger" onClick={() => removePage(page)}>{t('Delete')}</Button></div></div>)}</Card>}</section>}

          {activeTab === 'appearance' && <BlogConfigForm blog={blog} onSaved={setBlog}/>}
        </div>}
      </main>
    </div>
    <PostEditorModal open={editorOpen} post={editingPost} onClose={() => setEditorOpen(false)} onSaved={savePost}/>
    <PageEditorModal open={pageEditorOpen} page={editingPage} onClose={() => setPageEditorOpen(false)} onSaved={savePage}/>
  </div>;
}

export default TeacherBlog;
